import {
  call,
  put,
  select,
  StrictEffect,
  takeEvery,
  takeLatest
} from "redux-saga/effects";

import { Paginated } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";
import { AppError, extractError } from "@/jenesys-types/errors";
import {
  Client,
  Company,
  RefactoredCompany,
  User
} from "@/jenesys-types/jenesys-entities/config";

import { list, performDataAction } from "@/services/api";
import { loadAccountingData } from "@/store/features/clientSettings/connections/connections";
import {
  changeClient,
  ChangeClientAction,
  changeClientSuccess,
  changeCompany,
  ChangeCompanyAction,
  changeCompanySuccess,
  loadClients,
  selectActiveCompanyId,
  selectClients,
  setActiveClientEntity,
  setActiveCompanyEntity,
  setActiveLocation,
  setCompany
} from "@/store/features/companies/companies";

import {
  acceptTermsOfService,
  AcceptTermsofServiceAction,
  acceptTermsOfServiceFailure,
  acceptTermsOfServiceSuccess,
  bootstrapApp,
  bootstrapClientSuccess,
  bootstrapFailure,
  BootstrapFailureReason,
  bootstrapSuccess,
  clearHubSpotCache,
  ClearHubSpotCacheAction,
  selectCompanies,
  selectCurrentUser,
  selectStoredClients,
  setupHubSpot,
  SetupHubSpotAction,
  setupHubSpotFailure,
  setupHubSpotSuccess,
  updateUser,
  UpdateUserAction,
  updateUserFailure,
  updateUserSuccess
} from "./bootstrap";

const URL_COMPANY_ID_REGEX = new RegExp(`/company/([^/]*)`);
const URL_CLIENT_ID_REGEX = new RegExp(`/client/([^/]*)`);
const LOCAL_STORAGE_STATUS_KEY = "lastDocumentNavigationStatus";
const HUBSPOT_TOKEN_STORAGE_KEY = "hubspotTokenData";
const HUBSPOT_TOKEN_EXPIRY_HOURS = 12;

interface CachedHubSpotToken {
  email: string;
  token: string;
  timestamp: number;
}

const getCachedHubSpotToken = (
  userEmail: string
): CachedHubSpotToken | null => {
  try {
    const cachedData = localStorage.getItem(HUBSPOT_TOKEN_STORAGE_KEY);
    if (!cachedData) return null;

    const cached: CachedHubSpotToken = JSON.parse(cachedData);
    const tokenAge = Date.now() - cached.timestamp;
    const expiryTime = HUBSPOT_TOKEN_EXPIRY_HOURS * 60 * 60 * 1000; // Convert hours to milliseconds

    // Return cached token if email matches and token is not expired
    if (cached.email === userEmail && tokenAge < expiryTime) {
      return cached;
    }

    return null;
  } catch {
    return null;
  }
};

const saveHubSpotTokenToCache = (email: string, token: string): void => {
  const tokenData: CachedHubSpotToken = {
    email,
    token,
    timestamp: Date.now()
  };

  localStorage.setItem(HUBSPOT_TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
};

const isCompanyIdValid = (
  companyId: string,
  availableCompanies: Array<Company>
) => availableCompanies.some((company) => company.id === companyId);

function* loadRequiredDataForCompany(
  companyId: string
): Generator<StrictEffect, void, any> {
  yield put(loadClients(companyId));
  yield put(loadAccountingData(companyId));
}

function* loadRequiredDataForClients(
  clientId: string
): Generator<StrictEffect, void, any> {
  yield put(loadAccountingData(clientId));
}

export function* changeCompanysSaga(
  action: ChangeCompanyAction
): Generator<StrictEffect, void, any> {
  const companies = yield select(selectCompanies);
  const company =
    companies.length > 0
      ? companies.find(
          (companyToFind: Company) =>
            companyToFind.id === action.payload.companyId
        )
      : null;

  if (company) {
    localStorage.setItem("activeCompanyId", company.id);
    yield call(changeCompany, company);
    yield put(setActiveCompanyEntity(company));
    yield put(changeCompanySuccess(company));
    yield put(setCompany(company.id));
    yield put(setActiveLocation(company.id));
    yield call(loadRequiredDataForCompany, company.id);
  }
}

export function* changeClientSaga(
  action: ChangeClientAction
): Generator<StrictEffect, void, any> {
  const { clientId } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  const allClients: Paginated<Client> = yield select(selectClients);
  const storedClients: Client[] = yield select(selectStoredClients);
  const client = [
    ...(allClients ? allClients.items : []),
    ...storedClients
  ].find((clientToFind: any) => clientToFind.id === clientId);

  if (client) {
    yield put(setActiveClientEntity(client));
    yield call(changeClient, clientId);
    yield put(setCompany(clientId));
    yield put(setActiveLocation(companyId, clientId));
    yield call(loadRequiredDataForClients, clientId);
    yield put(changeClientSuccess(client));
    localStorage.setItem("activeClientId", clientId);
  }
}

export function* bootstrapAppSaga(): Generator<StrictEffect, void, any> {
  const minorErrors = new Array<AppError>();
  localStorage.setItem(LOCAL_STORAGE_STATUS_KEY, "REVIEWED");
  // Extract URL parameters first
  const [_, urlCompanyId] =
    URL_COMPANY_ID_REGEX.exec(window.location.pathname) || [];
  const [_1, urlClientId] =
    URL_CLIENT_ID_REGEX.exec(window.location.pathname) || [];

  // Get user data
  const user: User = yield call(list, DataType.User, {});
  const LSCompanyId = localStorage.getItem("activeCompanyId");

  let initialCompanyId = "";

  try {
    // First validate and handle company ID from URL

    if (urlCompanyId) {
      if (isCompanyIdValid(urlCompanyId, user.companies)) {
        localStorage.setItem("activeCompanyId", urlCompanyId);
        initialCompanyId = urlCompanyId;
        localStorage.setItem("activeCompanyId", urlCompanyId);
      } else {
        yield put(
          bootstrapFailure(
            BootstrapFailureReason.InvalidCompanyId,
            undefined,
            undefined
          )
        );
        return;
      }
    } else {
      // If no company ID in URL, use localStorage or first company
      initialCompanyId = LSCompanyId || user.companies[0].id;
      localStorage.setItem("activeCompanyId", initialCompanyId);
    }

    // Load required data for the company
    yield call(loadRequiredDataForCompany, initialCompanyId);

    // Handle client ID from URL if present
    if (urlClientId) {
      localStorage.setItem("activeClientId", urlClientId);

      // Load client data using correct data type
      const client: Client = yield call(
        performDataAction,
        DataType.Company,
        "list",
        {
          companyId: urlClientId,
          id: urlClientId
        }
      );

      if (client) {
        yield put(bootstrapClientSuccess(client));
        yield put(setActiveClientEntity(client));
        yield call(loadRequiredDataForClients, urlClientId);
      }
    }

    // Store propel auth ID if we have a valid company
    const initialCompany = user.companies.find(
      (company) => company.id === initialCompanyId
    );
    if (initialCompany) {
      localStorage.setItem("activeCompanyPropelAuthId", user?.propel_auth_id);
      yield put(setActiveCompanyEntity(initialCompany as RefactoredCompany));
    }
  } catch (error) {
    yield put(
      bootstrapFailure(
        BootstrapFailureReason.CompanyDataLoadingFailure,
        extractError(error),
        initialCompanyId
      )
    );
    return;
  }

  yield put(bootstrapSuccess(initialCompanyId, user, minorErrors));

  // Setup HubSpot chat after successful bootstrap (asynchronously)
  yield put(setupHubSpot());
}

export function* updateUserSaga(
  action: UpdateUserAction
): Generator<StrictEffect, void, any> {
  const { user, onSuccess } = action.payload;
  try {
    yield call(
      performDataAction,
      DataType.User,
      "updateUserMethod",
      {},
      undefined,
      { values: user }
    );
    yield put(updateUserSuccess(user));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(updateUserFailure(extractError(error)));
  }
}

export function* acceptTermsOfServiceSaga(
  action: AcceptTermsofServiceAction
): Generator<StrictEffect, void, any> {
  const { value, onSuccess } = action.payload;
  try {
    yield call(
      performDataAction,
      DataType.User,
      "updateUserMethod",
      {},
      undefined,
      value
    );
    yield put(acceptTermsOfServiceSuccess(value));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(acceptTermsOfServiceFailure(extractError(error)));
  }
}

export function* setupHubSpotSaga(
  action: SetupHubSpotAction
): Generator<StrictEffect, void, any> {
  const { onSuccess } = action.payload;

  try {
    const user: User = yield select(selectCurrentUser);
    const currentUserEmail = user?.email;

    if (!currentUserEmail) {
      throw new Error("No user email available for HubSpot setup");
    }

    // Check for cached token first
    const cachedToken = getCachedHubSpotToken(currentUserEmail);
    let hubSpotToken: {
      email: string;
      token: string;
    };

    if (cachedToken) {
      hubSpotToken = {
        email: cachedToken.email,
        token: cachedToken.token
      };
    } else {
      hubSpotToken = yield call(
        performDataAction,
        DataType.HubSpotToken,
        "generate",
        {}
      );

      saveHubSpotTokenToCache(hubSpotToken.email, hubSpotToken.token);
    }

    window.hsConversationsSettings = {
      identificationEmail: hubSpotToken.email,
      identificationToken: hubSpotToken.token
    };

    window.HubSpotConversations?.widget?.load?.();

    yield put(setupHubSpotSuccess(hubSpotToken.email, hubSpotToken.token));
    if (onSuccess) onSuccess();
  } catch (error) {
    // Even if the API request fails, still try to load the widget
    if (window.HubSpotConversations?.widget?.load) {
      window.HubSpotConversations.widget.load();
    }

    yield put(setupHubSpotFailure(extractError(error)));
  }
}

export function* clearHubSpotCacheSaga(
  _action: ClearHubSpotCacheAction
): Generator<StrictEffect, void, any> {
  localStorage.removeItem(HUBSPOT_TOKEN_STORAGE_KEY);
}

export default function* appSaga() {
  yield takeLatest(bootstrapApp.type, bootstrapAppSaga);
  yield takeEvery(changeCompany.type, changeCompanysSaga);
  yield takeEvery(changeClient.type, changeClientSaga);
  yield takeEvery(updateUser.type, updateUserSaga);
  yield takeEvery(acceptTermsOfService.type, acceptTermsOfServiceSaga);
  yield takeEvery(setupHubSpot.type, setupHubSpotSaga);
  yield takeEvery(clearHubSpotCache.type, clearHubSpotCacheSaga);
}
